import { nonProdLogger } from "../nonProdLogger"
import { DidomiAPI } from "./types"

let deferredUntilConsentFunctions: (() => void)[] = []

// Track whether Didomi is believed to be enabled or disabled
// It defaults to the safest, true, but this might not be correct.
let didomiCurrentlyEnabled: boolean = true

/**
 * This function should be called to indicate the Didomi status in the
 * current region. (eg. from GdproConsentStatus.tsx in React, or a page
 * entry function in Rails.)
 *
 * If disabled, it will run functions that were asked to be deferred until consent was given,
 * as without Didomi existing, the window.didomiOnReady functions will never run.
 */
export function setDidomiEnabledStatus(status: boolean) {
  didomiCurrentlyEnabled = status
  if (status == false) runAllDeferredFunctions()
}

function runAllDeferredFunctions() {
  while (deferredUntilConsentFunctions.length > 0) {
    const nextFn = deferredUntilConsentFunctions.shift()
    try {
      if (nextFn) nextFn()
    } catch (err: unknown) {
      if (err instanceof Error) {
        nonProdLogger(`Error running deferred Didomi-related function: ${err.message}`)
      } else {
        nonProdLogger("Error running deferred Didomi-related function")
      }
    }
  }
}

/**
 * Only runs the given function once consent has been given by the user
 * to track them with Braze. It's not actually super efficient if this
 * gets called a lot of times, as it registers another `observeConsentStatusChange`
 * callback every time. However it will avoid running the deferred functions
 * more than once.
 */
export function deferUntilPermitted(vendorId: string, purpose: string, callback: () => void) {
  // if Didomi has been disabled in this region, just run the function straight away:
  if (!didomiCurrentlyEnabled) {
    callback()
    return
  }

  deferredUntilConsentFunctions.push(callback)

  consentStatus({ vendorId, purpose }).then((status) => {
    if (status) runAllDeferredFunctions()
    else {
      observeConsentStatusChange(() => {
        consentStatus({ vendorId, purpose }).then((status) => {
          if (status) runAllDeferredFunctions()
        })
      })
    }
  })
}

/**
 * Check if the current user has given consent for a specific purpose and vendor.
 * This function uses the `window.didomiOnReady` array to ensure that the Didomi SDK is initialized
 * before checking the consent status.
 *
 * @param vendorId The vendor ID to check. E.g.: `c:theconver-dUNGGyiP`
 * @param purpose - The purpose to check. E.g.: `theconver-AA8pWL4T`
 * @return {Promise<boolean>}
 * @fullfill {boolean} true if the user has given consent, false otherwise
 * @fullfill {boolean} false if the Didomi SDK is not ready after a timeout.
 */
export const consentStatus = async ({
  vendorId,
  purpose,
}: {
  vendorId: string
  purpose: string
}): Promise<boolean> => {
  return new Promise((resolve) => {
    window.didomiOnReady = window.didomiOnReady || []

    /**
     * The `window.didomiOnReady` is an array that will be populated with a function when Didomi JS is
     * initialized. We resolve its `Didomi` when it's ready, or assume that users rejected it if Didomi has timed out.
     *
     * @see https://developers.didomi.io/cmp/web-sdk/reference/api#didomi-ready
     */
    const timeoutId = setTimeout(() => {
      console.warn("Didomi SDK did not initialize in time, assuming user has not given consent")
      resolve(false)
    }, 4000)

    window.didomiOnReady.push((Didomi: DidomiAPI) => {
      clearTimeout(timeoutId)

      try {
        const status = Didomi.getCurrentUserStatus()

        const userConsentWithVendorAndPurpose =
          status.vendors?.[vendorId]?.enabled && status.purposes?.[purpose]?.enabled

        return resolve(!!userConsentWithVendorAndPurpose)
      } catch (error) {
        console.warn(
          "Error getting Didomi user status, assuming user has not given consent:",
          error,
        )
        return resolve(false)
      }
    })
  })
}

/**
 * Adds an event listener when user updates their Didomi consent status, pushing a callback function
 * to the `window.didomiEventListeners` array.
 * @param callback
 */
export const observeConsentStatusChange = (callback: Function) => {
  window.didomiEventListeners = window.didomiEventListeners || []
  window.didomiEventListeners.push({
    event: "consent.changed",
    /**
     * The `consent.changed` listener context is the action that triggered the consent change, which
     * can be one of: `click`, `navigate`, or `external`.
     * We are not using the `action` for now, but only keeping it documented it's available in this event.
     * @see https://developers.didomi.io/cmp/web-sdk/reference/events#consent.changed
     * @param action
     */
    listener: ({ action: _action }: { action: "click" | "navigate" | "external" }) => {
      callback()
    },
  })
}
